
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, PenSquare, BookOpen, ChevronDown, CreditCard } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface HeaderProfileProps {
  displayName: string;
  profileImage: string | null;
  notificationCount: number;
  subscriptionStatus?: string;
  walletBalance?: number;
  draftCount?: number;
  isJournalist: boolean;
  isInReaderMode: boolean;
  isInCreatorMode: boolean;
}

const HeaderProfile = ({
  displayName,
  profileImage,
  notificationCount,
  subscriptionStatus,
  walletBalance,
  draftCount,
  isJournalist,
  isInReaderMode,
  isInCreatorMode
}: HeaderProfileProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      toast({
        title: 'Logged out successfully',
        description: 'You have been logged out of your account',
      });
    } catch (error) {
      console.error('Error logging out:', error);
      toast({
        variant: 'destructive',
        title: 'Logout failed',
        description: 'There was a problem logging out. Please try again.',
      });
    }
  };

  const handleSwitchMode = () => {
    console.log('HeaderProfile - Current location:', location.pathname);
    console.log('HeaderProfile - isInReaderMode:', isInReaderMode);
    console.log('HeaderProfile - isInCreatorMode:', isInCreatorMode);
    
    if (isInReaderMode) {
      console.log('HeaderProfile - Switching to Creator mode');
      navigate('/dashboard');
      toast({
        title: "Creator Mode",
        description: "You're now in Creator mode"
      });
    } else if (isInCreatorMode) {
      console.log('HeaderProfile - Switching to Reader mode');
      navigate('/home');
      toast({
        title: "Reader Mode",
        description: "You're now in Reader mode"
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center space-x-2 p-1 rounded-full hover:bg-[#F5F5F7] transition-colors duration-200">
          <Avatar className="h-8 w-8 border-2 border-transparent">
            <AvatarImage src={profileImage || undefined} />
            <AvatarFallback>{displayName.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <ChevronDown size={16} className="text-[#1A2033] hidden md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 mt-1">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span>{displayName}</span>
            <span className="text-xs text-muted-foreground">
              {!isJournalist 
                ? `${subscriptionStatus || 'Free'} • ${walletBalance ? `$${walletBalance.toFixed(2)} wallet` : 'No wallet'}` 
                : `${draftCount} draft${draftCount !== 1 ? 's' : ''}`}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem asChild>
          <Link to="/profile">Profile</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/subscription">
            <CreditCard className="mr-2 h-4 w-4" />
            Subscription
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings">Account Settings</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/help">Help & Support</Link>
        </DropdownMenuItem>
        
        {isJournalist && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSwitchMode} className="cursor-pointer">
              {isInReaderMode ? (
                <>
                  <PenSquare className="mr-2 h-4 w-4" />
                  Switch to Creator
                </>
              ) : (
                <>
                  <BookOpen className="mr-2 h-4 w-4" />
                  Switch to Reader
                </>
              )}
            </DropdownMenuItem>
          </>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-500 focus:text-red-500 cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HeaderProfile;
